<template>
  <div
    v-if="hasOverlay"
    class="overlay-container absolute inset-0 flex items-center justify-center w-full h-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 clamp-px-4-8 clamp-py-8-16 overflow-hidden"
    :class="{ 'opacity-100': isOverlayVisible }"
    :style="{ backgroundColor: primaryColor?.hex }"
    @click.stop>
    <div class="overlay-content">
      <div v-if="hasOverlayImage" class="flex justify-center">
        <AImage
          class="max-w-full max-h-full object-contain clamp-h-15-40 w-auto"
          :src="props.blok.overlay_image?.filename || ''"
          :alt="props.blok.overlay_image?.alt"
          :focalPoint="props.blok.overlay_image?.focus"
          ratio="orig"
          :sizes="sizes"
        />
      </div> 
      <div class="mr-auto flex flex-col justify-start">
        <p v-if="hasOverlayText" class="overlay-text">
          {{props.blok.overlay_text}}
        </p>
      </div>
    </div> 
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { ElementCardsCardStoryblok } from "@/types";
import { ImgSizes } from "@/components/atoms/AImage.vue";
import useConfig from "@/composables/useConfig";

interface Props {
  blok: ElementCardsCardStoryblok;
  sizes: ImgSizes;
  isOverlayVisible: boolean;
}

const props = defineProps<Props>();
const { primaryColor } = useConfig();

const hasOverlayImage = computed(() => {
  return !!(props.blok.overlay_image && props.blok.overlay_image.filename);
});

const hasOverlayText = computed(() => {
  return !!props.blok.overlay_text?.trim();
});

const hasOverlay = computed(() => {
  return hasOverlayImage.value || hasOverlayText.value;
});
</script>

<style lang="scss">
.overlay-container {
  container-type: inline-size;
}

@container (max-width: 150px) {
  .overlay-content {
    @apply space-y-2;
  }
  .overlay-text {
    @apply text-body-xs;
  }
}

@container (min-width: 150px) and (max-width: 200px) {
  .overlay-text {
    @apply text-body-xs;
  }
  .overlay-content {
    @apply space-y-4;
  }
}

@container (min-width: 200px) and (max-width: 300px) {
  .overlay-text {
    @apply text-body-sm;
  }
  .overlay-content {
    @apply space-y-6;
  }
}

@container (min-width: 300px) {
  .overlay-text {
    @apply text-body-md;
  }
  .overlay-content {
    @apply space-y-8;
  }
}
</style>
